import * as React from 'react';
import { Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useSupplyAppsQuery } from '../../hooks';
import {
  formatSupplyDate,
  resolveSupplyStatusLabel,
  resolveSupplyStatusTone,
  supplyPageSizes,
  type SupplyApp,
} from '../../model';
import { useSupplyAppsRouteFilters } from '../../useSupplyRouteFilters';
import { SupplyAppDialog } from '../SupplyAppDialog';
import { SupplyTableRowActions } from '../SupplyTableRowActions';

export function SupplyAppsTable() {
  const { can } = useShigoPermissions();
  const canWrite = can(ShigoPermission.MadBuySupplyWrite);
  const { filter, setSearch, setPagination } = useSupplyAppsRouteFilters();
  const [dialogApp, setDialogApp] = React.useState<SupplyApp | null | undefined>();
  const query = useSupplyAppsQuery(filter);
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<SupplyApp>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supply.name'),
          headerClassName: 'pl-4',
          className: 'pl-4 font-medium',
          value: (item) => item.name,
        },
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('supply.status'),
          label: (item) => resolveSupplyStatusLabel(item.status),
          tone: (item) => resolveSupplyStatusTone(item.status),
        },
        {
          id: 'bundleId',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supply.bundleId'),
          value: (item) => item.bundleId,
        },
        {
          id: 'appStoreId',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supply.appStoreId'),
          value: (item) => item.appStoreId,
        },
        {
          id: 'created',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('supply.created'),
          headerClassName: 'w-28',
          className: 'w-28 whitespace-nowrap',
          value: (item) => formatSupplyDate(item.createdOn),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canWrite,
          cell: (item) => (
            <SupplyTableRowActions
              onEdit={() => {
                setDialogApp(item);
              }}
            />
          ),
        },
      ]),
    [canWrite],
  );

  return (
    <>
      <DashboardDataTable
        title={dashboardT('supply.apps.title')}
        tableLabel={dashboardT('supply.apps.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(item) => item.id}
        loading={query.isLoading}
        errorMessage={query.isError ? dashboardT('supply.loadError') : undefined}
        emptyMessage={dashboardT('supply.apps.empty')}
        minWidthClassName="min-w-[880px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('supply.search')}
              value={filter.searchTerm}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            {canWrite ? (
              <Button
                onClick={() => {
                  setDialogApp(null);
                }}
              >
                <Plus className="h-4 w-4" />
                {dashboardT('supply.apps.create')}
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: supplyPageSizes,
          disabled: query.isLoading,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
      />
      {canWrite && dialogApp !== undefined ? (
        <SupplyAppDialog
          app={dialogApp}
          onClose={() => {
            setDialogApp(undefined);
          }}
        />
      ) : null}
    </>
  );
}
